import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import DateTimePicker from "../components/DateTimePicker";
import FacilityPicker from "../components/FacilityPicker";
import GolfFormatFields from "../components/GolfFormatFields";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";

const LEVELS = ["all", "beginner", "intermediate", "advanced"];

export default function CreateMatch() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [sports, setSports] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [form, setForm] = useState({
    sport_id: "", club_id: "", facility_id: null, title: "", description: "",
    visibility: "public", match_type: "public", skill_level: "all", max_players: 4,
    start_time: "", end_time: "", all_day: false, price_per_player: 0, currency: "EUR", location: "",
    golf: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/sports").then((r) => { setSports(r.data); if (r.data[0]) setForm((f) => ({ ...f, sport_id: r.data[0].id })); });
    api.get("/clubs").then((r) => setClubs(r.data));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Detect when the chosen sport is golf — then show the golf-specific block.
  const selectedSport = useMemo(() => sports.find((s) => s.id === form.sport_id), [sports, form.sport_id]);
  const isGolf = selectedSport?.slug === "golf";
  // Auto-init golf config the first time the user picks golf, clear when they switch away.
  useEffect(() => {
    if (isGolf && !form.golf) setForm((f) => ({ ...f, golf: { format: "stroke", holes: 18, team_size: 1, handicap_allowance: 1.0, course_par: 72, side_games: [] } }));
    else if (!isGolf && form.golf) setForm((f) => ({ ...f, golf: null }));
  }, [isGolf, form.golf]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_time) { toast.error("Pick a start date/time"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        match_type: form.visibility, // mirror to legacy field
        max_players: Number(form.max_players),
        price_per_player: Number(form.price_per_player),
        start_time: new Date(form.start_time).toISOString(),
        end_time: form.end_time ? new Date(form.end_time).toISOString() : null,
        club_id: form.club_id || null,
        golf: isGolf ? form.golf : null,
      };
      const { data } = await api.post("/matches", payload);
      toast.success("Match created");
      navigate(`/matches/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <h1 className="font-heading text-3xl sm:text-4xl font-black">{t("create_match")}</h1>
      <p className="text-zinc-400 mt-2">{t("create_match_subtitle")}</p>

      <form onSubmit={submit} className="mt-8 max-w-2xl pp-card-elevated p-8 space-y-4" data-testid="create-match-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("sport")}>
            <select data-testid="cm-sport" required value={form.sport_id} onChange={set("sport_id")} className="pp-input">
              {sports.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Club">
            <select data-testid="cm-club" value={form.club_id} onChange={set("club_id")} className="pp-input">
              <option value="">—</option>
              {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t("title")}>
          <input data-testid="cm-title" required value={form.title} onChange={set("title")} className="pp-input" />
        </Field>
        <Field label={t("description")}>
          <textarea data-testid="cm-description" rows={3} value={form.description} onChange={set("description")} className="pp-input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("skill_level")}>
            <select data-testid="cm-level" value={form.skill_level} onChange={set("skill_level")} className="pp-input">
              {LEVELS.map((l) => <option key={l} value={l}>{t(`level_${l}`)}</option>)}
            </select>
          </Field>
          <Field label={t("max_players")}>
            <input data-testid="cm-maxplayers" type="number" min={2} max={32} value={form.max_players} onChange={set("max_players")} className="pp-input" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`${t("starts")}`}>
            <DateTimePicker data-testid="cm-start" withTime={!form.all_day} value={form.start_time} onChange={(v) => setForm((f) => ({ ...f, start_time: v }))} />
          </Field>
          <Field label={t("ends")}>
            <DateTimePicker data-testid="cm-end" withTime={!form.all_day} value={form.end_time} onChange={(v) => setForm((f) => ({ ...f, end_time: v }))} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={form.all_day} onChange={(e) => setForm((f) => ({ ...f, all_day: e.target.checked }))} data-testid="cm-allday" />
          All-day event (no specific start/finish time)
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("price_per_player")}>
            <input data-testid="cm-price" type="number" step={0.5} min={0} value={form.price_per_player} onChange={set("price_per_player")} className="pp-input" />
          </Field>
          <Field label={t("location")}>
            <FacilityPicker
              sportSlug={(sports.find((s) => s.id === form.sport_id) || {}).slug}
              value={{ facility_id: form.facility_id, location: form.location }}
              onChange={({ facility_id, location }) => setForm((f) => ({ ...f, facility_id, location }))}
              testIdPrefix="cm-facility"
            />
          </Field>
        </div>
        <Field label="Visibility">
          <div className="flex gap-2" data-testid="cm-visibility">
            <button type="button" data-testid="cm-vis-public" onClick={() => setForm((f) => ({ ...f, visibility: "public" }))}
              className={`pp-chip flex-1 ${form.visibility === "public" ? "pp-chip-active" : ""}`}>
              Public · listed in search
            </button>
            <button type="button" data-testid="cm-vis-private" onClick={() => setForm((f) => ({ ...f, visibility: "private" }))}
              className={`pp-chip flex-1 ${form.visibility === "private" ? "pp-chip-active" : ""}`}>
              Private · invite/QR only
            </button>
          </div>
          <p className="text-xs text-zinc-500 mt-1">{form.visibility === "private" ? "Hidden from public lists. Only people you invite or who scan your QR can join." : "Anyone can find and join this match."}</p>
        </Field>
        {isGolf && <GolfFormatFields value={form.golf} onChange={(g) => setForm((f) => ({ ...f, golf: g }))} variant="match" />}
        <div className="flex gap-3 pt-2">
          <button data-testid="cm-submit" disabled={loading} className="pp-btn-primary px-6 py-3 rounded-xl">{loading ? "..." : t("create")}</button>
          <button type="button" data-testid="cm-cancel" onClick={() => navigate(-1)} className="pp-btn-secondary px-6 py-3 rounded-xl">{t("cancel")}</button>
        </div>
      </form>
    </AppLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{label}</div>
      {children}
    </label>
  );
}
